import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import StatusBadge from "@/components/base/StatusBadge";
import JsonLd from "@/components/feature/JsonLd";
import { usePageMeta } from "@/hooks/usePageMeta";
import { events } from "@/mocks/events";

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const event = events.find((item) => item.id === id);

  usePageMeta({
    title: event
      ? `${event.title} - 争议事件追踪 | 爆料直播`
      : "争议事件追踪 | 爆料直播",
    description: event
      ? `${event.summary.slice(0, 90)}…平台核查进度、争议焦点与平台处理结果全程公开，可回看事件时间线。`
      : "爆料直播争议事件追踪，公开事件立案时间、争议焦点、核查进度与平台处理结果。",
    keywords: event
      ? `${event.category},直播争议事件,爆料直播,平台处理结果`
      : "直播争议事件,爆料直播,平台处理结果",
    path: `/events/${id ?? ""}`,
  });

  if (!event) {
    return (
      <SiteLayout>
        <section className="w-full px-4 md:px-8 lg:px-12 py-32 text-center">
          <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl md:text-3xl font-bold text-foreground-950">未找到该争议事件</h1>
          <p className="mt-3 text-sm text-foreground-600">该事件可能已被合并或链接有误，你也可以返回列表查看全部在跟踪的事件。</p>
          <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/events"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-list-check-2" aria-hidden="true"></i>
              返回事件列表
            </Link>
            <Link
              to="/report"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-medium border border-foreground-300 text-foreground-900 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              提交新的举报线索
            </Link>
          </div>
        </section>
      </SiteLayout>
    );
  }

  const relatedEvents = events.filter((item) => event.related.includes(item.id));

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: event.title,
    description: event.summary,
    datePublished: event.date,
    dateModified: event.updatedAt,
    articleSection: event.category,
    inLanguage: "zh-CN",
    keywords: event.tags.join(","),
    about: event.tags,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页" },
      { "@type": "ListItem", position: 2, name: "争议事件" },
      { "@type": "ListItem", position: 3, name: event.title },
    ],
  };

  return (
    <SiteLayout>
      <JsonLd id="ld-event-article" data={articleSchema} />
      <JsonLd id="ld-event-breadcrumb" data={breadcrumbSchema} />

      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 lg:px-12 pt-24 md:pt-28 pb-10 md:pb-12">
          <Breadcrumbs
            items={[
              { label: "首页", path: "/" },
              { label: "争议事件", path: "/events" },
              { label: event.title },
            ]}
          />

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-100 text-primary-900 border border-primary-200 text-xs font-medium">
              <i className="ri-price-tag-3-line" aria-hidden="true"></i>
              {event.category}
            </span>
            <StatusBadge status={event.status} />
            <span className="text-xs text-foreground-500">立案 {event.date} · 更新 {event.updatedAt}</span>
          </div>

          <h1 className="mt-4 max-w-4xl font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug text-balance">
            {event.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-600">
            <span className="inline-flex items-center gap-2">
              <i className="ri-live-line text-foreground-400" aria-hidden="true"></i>
              {event.platform}
            </span>
            <span className="inline-flex items-center gap-2">
              <i className="ri-mic-line text-foreground-400" aria-hidden="true"></i>
              {event.room}
            </span>
            <span className="inline-flex items-center gap-2">
              <i className="ri-flag-2-line text-foreground-400" aria-hidden="true"></i>
              收到 {event.reports} 条反馈
            </span>
            <span className="inline-flex items-center gap-2">
              <i className="ri-eye-line text-foreground-400" aria-hidden="true"></i>
              {event.views} 次关注
            </span>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="relative -mt-1 w-full h-56 md:h-80 overflow-hidden rounded-b-lg">
          <img
            src={event.cover}
            alt={`${event.title} 事件配图`}
            title={`${event.title} - ${event.category}`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <article className="lg:col-span-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">事件概览</h2>
            <p className="mt-4 text-sm md:text-base leading-7 text-foreground-700">{event.summary}</p>

            <h2 className="mt-10 font-heading text-xl md:text-2xl font-bold text-foreground-950">争议焦点</h2>
            <ul className="mt-4 space-y-3">
              {event.focus.map((item) => (
                <li key={item} className="flex gap-3 text-sm md:text-base leading-relaxed text-foreground-700">
                  <i className="ri-question-line mt-1 text-primary-500 shrink-0" aria-hidden="true"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <h2 className="mt-10 font-heading text-xl md:text-2xl font-bold text-foreground-950">事件时间线</h2>
            <ol className="mt-5 border-l border-background-300 pl-5 md:pl-6 space-y-6">
              {event.timeline.map((node) => (
                <li key={node.time} className="relative">
                  <span
                    className="absolute -left-[27px] md:-left-[31px] top-1 w-2.5 h-2.5 rounded-full bg-primary-500 ring-4 ring-primary-100"
                    aria-hidden="true"
                  ></span>
                  <p className="font-heading text-sm font-bold text-primary-700">{node.time}</p>
                  <h3 className="mt-1 text-base font-medium text-foreground-950">{node.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">{node.desc}</p>
                </li>
              ))}
            </ol>

            <h2 className="mt-10 font-heading text-xl md:text-2xl font-bold text-foreground-950">平台处理结果</h2>
            <div className="mt-4 bg-accent-50 border border-accent-200 rounded-lg p-5 md:p-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-900 border border-accent-200 text-sm font-medium">
                <i className="ri-scales-3-line" aria-hidden="true"></i>
                {event.resultType}
              </span>
              <p className="mt-4 text-sm md:text-base leading-7 text-foreground-700">{event.result}</p>
            </div>
          </article>

          <aside className="lg:col-span-4 space-y-6">
            <div className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">事件信息</h2>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">事件编号</dt>
                  <dd className="font-heading text-foreground-950">{event.id}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">直播平台</dt>
                  <dd className="text-foreground-950">{event.platform}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">直播间</dt>
                  <dd className="text-foreground-950 text-right">{event.room}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">立案时间</dt>
                  <dd className="text-foreground-950">{event.date}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">最近更新</dt>
                  <dd className="text-foreground-950">{event.updatedAt}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">处置类型</dt>
                  <dd className="text-foreground-950">{event.resultType}</dd>
                </div>
              </dl>
              <div className="mt-4 pt-4 border-t border-background-200 flex flex-wrap gap-2">
                {event.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs"
                  >
                    <i className="ri-hashtag text-xs" aria-hidden="true"></i>
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-primary-600 text-background-50 rounded-lg p-5 md:p-6">
              <h2 className="font-heading text-base font-bold">遇到同类争议？</h2>
              <p className="mt-2 text-sm leading-relaxed text-background-100/90">
                提交线索后可获得受理编号，24 小时内确认受理，核查进度与处理结果全程同步。
              </p>
              <Link
                to="/report"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium bg-background-50 text-primary-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-live-line" aria-hidden="true"></i>
                进入举报通道
              </Link>
            </div>

            {relatedEvents.length > 0 && (
              <div className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">相关争议事件</h2>
                <ul className="mt-4 space-y-4">
                  {relatedEvents.map((item) => (
                    <li key={item.id}>
                      <Link to={`/events/${item.id}`} className="group block">
                        <span className="block text-sm font-medium leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors">
                          {item.title}
                        </span>
                        <span className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
                          <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                          {item.category}
                          <span className="text-foreground-300">·</span>
                          {item.date}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Link
              to="/events"
              className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors"
            >
              <i className="ri-arrow-left-line" aria-hidden="true"></i>
              返回全部争议事件
            </Link>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}